<script setup>
import { computed, watch , onMounted } from 'vue'
import { installTelegramDeepLinks } from '@/lib/telegramLinks'
import { usePage, router } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import ChatwootWidget from '@/Components/Public/ChatwootWidget.vue'
import HeaderMenu from '@/Components/Public/HeaderMenu.vue'
import ThemeMenu from '@/Components/Public/ThemeMenu.vue'
import PlatformIcon from '@/Components/Public/PlatformIcon.vue'
import { currencySymbol } from '@/lib/currencySymbols'
import BrandHeader from '@/Components/Public/BrandHeader.vue'
import PublicNav from '@/Components/Public/PublicNav.vue'
import SiteFooter from '@/Components/Public/SiteFooter.vue'

// Telegram links open the app directly on desktop (t.me is DNS-hijacked on some Thai ISPs).
onMounted(installTelegramDeepLinks)

const page = usePage()
const { t, locale } = useI18n()

const brand    = computed(() => page.props.brand || {})
const current  = computed(() => page.props.locale || 'en')
const flash    = computed(() => page.props.flash || {})
const partnersBase = computed(() => page.props.partners_base || 'https://partners.getphuket.com')



watch(current, (val) => { locale.value = val }, { immediate: true })

// The three public hosts (apex / villas / rentals) and which one we're on —
// shared by Public::BaseController; the /_p dev preview collapses them all
// to the /_p prefix.
const hosts = computed(() => page.props.public_hosts || { apex: '', villas: '', rentals: '', current: 'apex' })
// Absolute link into another public host, locale prefix preserved.
function hostPath(kind, path) {
  const base = hosts.value[kind] || ''
  if (base.startsWith('/')) return `${base}${path === '/' ? '' : path}`
  const loc = current.value
  const prefix = (!loc || loc === 'en') ? '' : `/${loc}`
  return `${base}${prefix}${path === '/' ? '' : path}` || '/'
}
const localePrefix = computed(() => (!current.value || current.value === 'en') ? '' : `/${current.value}`)


function setLang(lang) {
  if (lang === current.value) return
  const bare = window.location.pathname.replace(/^\/(ru|zh|th)(?=\/|$)/, '') || '/'
  const path = lang === 'en' ? bare : `/${lang}${bare === '/' ? '' : bare}`
  window.location.assign(`${path}?lang=${lang}`)
}

// Display-currency picker. Server honours ?currency= and persists in a
// cookie, so the next page load keeps the choice without us managing state.
// Derived from the shared public_hosts prop rather than window.location so
// the same code renders on the SSR side (no window there): "/_p" marks the
// localhost preview, the apex origin carries the domain.
const apexHost   = computed(() => page.props.public_hosts?.apex || '')
const isPreview  = computed(() => apexHost.value === '/_p')
const investUrl  = computed(() => isPreview.value ? '/_i' : (apexHost.value.endsWith('.ru') ? 'https://invest.getphuket.ru' : 'https://invest.getphuket.com'))
const paintingUrl = computed(() => isPreview.value ? '/_pt' : 'https://painting.getphuket.com')

const displayCurrency   = computed(() => page.props.display_currency || 'THB')
const displayCurrencies = computed(() => page.props.display_currencies || ['THB'])
const hasCurrencyPicker = computed(() => displayCurrencies.value.length > 1)

function setCurrency(code) {
  if (code === displayCurrency.value) return
  router.get(window.location.pathname, { currency: code }, { preserveScroll: true, preserveState: false })
}

const localeOptions = [
  { value: 'en', label: 'English', sub: 'EN' },
  { value: 'ru', label: 'Русский', sub: 'RU' },
  { value: 'zh', label: '中文',    sub: 'ZH' },
  { value: 'th', label: 'ไทย',     sub: 'TH' }
]
const currentLocaleLabel = computed(() => (localeOptions.find((o) => o.value === current.value) || localeOptions[0]).label)
</script>

<template>
  <div class="public-shell">
    <BrandHeader :home-href="hostPath('apex', '/')" :brand-label="brand.name || 'Get Phuket'">
      <template #nav>
        <PublicNav />
      </template>
      <template #tools>
        <div class="public-header__tools">
          <!-- Currency -->
          <HeaderMenu v-if="hasCurrencyPicker" :aria-label="t('public.menu.currency')" wide>
            <template #icon>
              <span class="hmenu__sym">{{ currencySymbol(displayCurrency) }}</span>
              <span class="hmenu__label">{{ displayCurrency }}</span>
            </template>
            <template #default="{ close }">
              <button v-for="code in displayCurrencies" :key="code" type="button" class="hmenu__item" @click="setCurrency(code); close()">
                <span><span class="hmenu__sym">{{ currencySymbol(code) }}</span> {{ code }}</span>
                <svg v-if="code === displayCurrency" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="hmenu__check" aria-hidden="true">
                  <polyline points="5 12 10 17 19 7"/>
                </svg>
              </button>
            </template>
          </HeaderMenu>

          <!-- Language -->
          <HeaderMenu :aria-label="t('public.menu.language')" wide>
            <template #icon>
              <PlatformIcon platform="globe" size="1.05rem" />
              <span class="hmenu__label">{{ currentLocaleLabel }}</span>
            </template>
            <template #default="{ close }">
              <button v-for="opt in localeOptions" :key="opt.value" type="button" class="hmenu__item" @click="setLang(opt.value); close()">
                <span>{{ opt.label }} <span class="hmenu__item-sub">({{ opt.sub }})</span></span>
                <svg v-if="opt.value === current" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="hmenu__check" aria-hidden="true">
                  <polyline points="5 12 10 17 19 7"/>
                </svg>
              </button>
            </template>
          </HeaderMenu>

          <!-- Theme -->
          <ThemeMenu />

          <PublicNav variant="menu" />
        </div>
      </template>
    </BrandHeader>

    <div v-if="flash.alert" role="alert" class="pub-flash pub-flash--alert">
      {{ flash.alert }}
    </div>
    <div v-if="flash.warn" role="status" class="pub-flash pub-flash--warn">
      {{ flash.warn }}
    </div>
    <div v-if="flash.notice" role="status" class="pub-flash pub-flash--notice">
      {{ flash.notice }}
    </div>

    <main class="public-main">
      <slot />
    </main>

    <SiteFooter :hosts="hosts" :channels="brand.channels" :invest-url="investUrl" :painting-url="paintingUrl" :partners-base="partnersBase" :locale-prefix="localePrefix" :home-href="hostPath('apex', '/')" :brand-name="brand.name || 'Get Phuket'" :locale="current" :locale-options="localeOptions" :currency="displayCurrency" :currencies="hasCurrencyPicker ? displayCurrencies : []" @select-lang="setLang" @select-currency="setCurrency" />
    <ChatwootWidget />
  </div>
</template>

<style scoped>
.public-shell {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background:
    radial-gradient(ellipse 80% 60% at 50% -10%, color-mix(in oklch, var(--accent) 12%, transparent) 0%, transparent 60%),
    var(--bg);
  color: var(--ink);
}

.public-main {
  flex: 1;
  display: block;
}


.pub-flash {
  padding: 0.75rem 1rem;
  margin: 1rem;
  border-radius: 0.5rem;
  font-size: 0.9375rem;
}
.pub-flash--alert {
  background: color-mix(in oklch, var(--danger) 12%, var(--surface));
  color: var(--danger);
  border: 1px solid color-mix(in oklch, var(--danger) 30%, transparent);
}
.pub-flash--warn {
  background: color-mix(in oklch, var(--warn) 12%, var(--surface));
  color: var(--warn);
  border: 1px solid color-mix(in oklch, var(--warn) 30%, transparent);
}
.pub-flash--notice {
  background: color-mix(in oklch, var(--ok) 12%, var(--surface));
  color: var(--ok);
  border: 1px solid color-mix(in oklch, var(--ok) 30%, transparent);
}
</style>
